import React, { useEffect, useState } from 'react';
import { Icon } from '../Icon/Icon';
import { ViewportOverlay } from '../Overlay/ViewportOverlay';
import { SetCheck, SetInput, SetSwitch } from '../SettingsShell/controls';
import { ROLES } from '../../settings-practice/types';
import type { Operatory } from '../../settings-practice/types';

export interface OperatoryEditModalProps {
  operatory: Operatory | null;
  roomIndex: number;
  roomCount: number;
  onClose: () => void;
  onSave: (next: Operatory) => void;
}

/**
 * Edit a room (spec 15 §6). A centre modal, explicit save, opened from any row —
 * there is no inline expansion, the list stays a list.
 *
 * Four things and only four: what it is called, who can work in it, whether it is
 * usable, and whether it catches the appointments nobody assigned. The board's
 * fourth field is the default room, not the "note" the prose lists; the board
 * wins on appearance and the list corroborates it with a `Default` chip.
 */
export function OperatoryEditModal({ operatory, roomIndex, roomCount, onClose, onSave }: OperatoryEditModalProps) {
  const [o, setO] = useState<Operatory | null>(operatory);
  useEffect(() => { setO(operatory); }, [operatory]);

  return (
    <ViewportOverlay open={operatory !== null} onClose={onClose}>
      {o ? (
        <div
          onClick={(e) => { e.stopPropagation(); }}
          className="flex max-h-[90vh] w-[min(620px,calc(100vw-32px))] flex-col overflow-hidden rounded-[18px] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[0_18px_48px_rgba(36,41,54,.18)]"
        >
          <div className="flex items-start justify-between gap-3 border-b border-[var(--border-default)] px-6 py-5">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-[11px] bg-[var(--brand-indigo-tint)]">
                <Icon kind="chair" size={17} stroke={1.9} style={{ color: 'var(--brand-indigo)' }} />
              </span>
              <div>
                <h2 className="m-0 text-[18px] font-bold tracking-[-0.02em] text-[var(--text-primary)]">{o.name}</h2>
                <p className="m-0 mt-0.5 text-[12.5px] text-[var(--text-muted)]">
                  Room {roomIndex} of {roomCount} · {o.outOfService ? 'out of service' : 'in service'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-8 w-8 items-center justify-center rounded-[10px] border border-[var(--border-default)] text-[var(--text-muted)] hover:bg-[var(--surface-sunken)]"
            >
              <Icon kind="x" size={14} stroke={2} />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            <Field
              label="Name"
              desc={
                <>
                  What staff see on the calendar.{' '}
                  <strong className="text-[var(--text-secondary)]">
                    Rename it to whatever this practice actually calls it
                  </strong>{' '}
                  — <em>&ldquo;the back room&rdquo;</em> beats <em>&ldquo;OP-03&rdquo;</em> when somebody is being asked
                  to walk a patient there.
                </>
              }
            >
              <div className="w-[280px]">
                <SetInput
                  ariaLabel="Room name"
                  value={o.name}
                  onChange={(v) => { setO({ ...o, name: v }); }}
                />
              </div>
            </Field>

            <Field
              label="Who can work in here"
              desc={
                <>
                  <strong className="text-[var(--text-secondary)]">The same eight roles as Providers.</strong> One
                  vocabulary: a reason routes to a role, a provider holds roles, a room accepts roles. Tick as many as
                  apply.
                </>
              }
            >
              <div className="flex flex-wrap gap-2">
                {ROLES.map((r) => (
                  <SetCheck
                    key={r.id}
                    checked={o.acceptsRoles.includes(r.id)}
                    onChange={(next) => {
                      setO({
                        ...o,
                        acceptsRoles: next
                          ? [...o.acceptsRoles, r.id]
                          : o.acceptsRoles.filter((x) => x !== r.id),
                      });
                    }}
                  >
                    {r.label}
                  </SetCheck>
                ))}
              </div>
            </Field>

            <Field
              label="In service"
              desc={
                <>
                  <strong className="text-[var(--text-secondary)]">
                    The one switch here that changes what ELVA does today.
                  </strong>{' '}
                  Off means one fewer place a patient can physically be, so ELVA stops counting this room when it works
                  out how much the practice can take.
                </>
              }
              control={
                <SetSwitch
                  label="In service"
                  checked={!o.outOfService}
                  onChange={(next) => { setO({ ...o, outOfService: !next }); }}
                />
              }
            />

            <Field
              last
              label="Use as the default room"
              desc={
                <>
                  An appointment that reaches its start with nobody having assigned a room lands here.{' '}
                  <strong className="text-[var(--text-secondary)]">One room carries it</strong> — turning it on here
                  turns it off wherever it is now.
                </>
              }
              control={
                <SetSwitch
                  label="Use as the default room"
                  checked={o.isDefault === true}
                  onChange={(next) => { setO({ ...o, isDefault: next }); }}
                />
              }
            />
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-[var(--border-default)] bg-[var(--surface-sunken)] px-6 py-4">
            <span className="text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
              <strong className="text-[var(--text-secondary)]">
                Rooms are created and removed in the practice management system
              </strong>
              , not here.
            </span>
            <span className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-2 text-[13.5px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-header)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { onSave(o); }}
                className="rounded-[10px] bg-[var(--brand-indigo)] px-4 py-2 text-[13.5px] font-semibold text-white"
              >
                Save
              </button>
            </span>
          </div>
        </div>
      ) : null}
    </ViewportOverlay>
  );
}

function Field({
  label,
  desc,
  control,
  children,
  last,
}: {
  label: string;
  desc: React.ReactNode;
  control?: React.ReactNode;
  children?: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div className="px-6 py-[18px]" style={last ? undefined : { borderBottom: '1px solid #edf0f6' }}>
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-bold text-[var(--text-primary)]">{label}</div>
          <p className="m-0 mt-1 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">{desc}</p>
        </div>
        {control ? <div className="shrink-0">{control}</div> : null}
      </div>
      {children ? <div className="mt-3">{children}</div> : null}
    </div>
  );
}
